// Use structured extraction assertions, not keywords or p-values inferred from prose.
const assertions = new Set([
  'reported_association', 'reported_prediction', 'non_significant',
  'tested_relationship', 'candidate_mediation', 'model_comparison',
]);

export function statisticalRecords(link) {
  return (link.description || '').split('\n').flatMap(line => {
    try {
      const record = JSON.parse(line);
      return record && assertions.has(record.assertion)
        && (!record.result_origin || (record.result_origin === 'current_study' && ['statistical', 'tested'].includes(record.result_kind)))
        && (!record.relationship_kind || record.relationship_kind === 'scientific')
        ? [record] : [];
    } catch { return []; }
  });
}

const endpoint = value => typeof value === 'object' ? value.id : value;
export const isMethodNode = node => (node.category_tags || [node.type]).includes('Methods & Study Design');
const parseAliases = value => {
  if (Array.isArray(value)) return value;
  try { return JSON.parse(value || '[]'); } catch { return []; }
};
const normalizedMethod = value => String(value || '').toLowerCase()
  .replace(/\bsem\b/g, 'structural equation model')
  .replace(/\banova\b/g, 'analysis of variance')
  .replace(/\bmodelling\b|\bmodeling\b|\bmodels\b/g, 'model')
  .replace(/[^a-z0-9]+/g, ' ').trim()
  .replace(/\s+study design$|\s+study$|\s+analysis$/g, '').trim();

export function methodLabels(node, provenance) {
  const entity = (provenance?.entities || []).find(item => item.graph_title?.toLowerCase() === String(node.id).toLowerCase());
  return [...new Set([node.id, node.name, node.title, ...(node.aliases || []),
    entity?.name, ...parseAliases(entity?.aliases)].filter(Boolean).map(normalizedMethod))];
}

export function recordMatchesMethod(record, labels) {
  const values = [record.analysis_type, record.study_design].filter(Boolean).map(normalizedMethod);
  return labels.some(label => values.some(value => label === value
    || (label.split(' ').length >= 2 && (` ${value} `).includes(` ${label} `))));
}

export function statisticalGraph(data, { method = null, analysis = null, design = null, provenance = null, methodNodes = data.nodes } = {}) {
  const methodIds = new Set(methodNodes.filter(node => isMethodNode(node) || (node.category_tags || [node.type]).some(tag => ['Study', 'Cohort', 'Context'].includes(tag))).map(node => node.id));
  const selected = methodNodes.find(node => node.id === method && isMethodNode(node));
  const labels = selected ? methodLabels(selected, provenance) : [];

  const links = data.links.flatMap(link => {
    if (methodIds.has(endpoint(link.source)) || methodIds.has(endpoint(link.target))) return [];
    const records = statisticalRecords(link).filter(record => (!method || (selected && recordMatchesMethod(record, labels))) && matchesStatisticalMetadata(record, { analysis, design }));
    return records.length ? [{ ...link, statisticalRecords: records,
      description: records.map(record => JSON.stringify(record)).join('\n'),
      weight: records.length }] : [];
  });
  const ids = new Set(links.flatMap(link => [endpoint(link.source), endpoint(link.target)]));
  return { nodes: data.nodes.filter(node => ids.has(node.id)), links };
}


export function metadataFilterKey(value, field = null) {
  const text = String(value || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  if (field === 'study_design' && ['prospective cohort', 'prospective cohort study'].includes(text)) return 'prospective cohort';
  return !text || text === 'not reported' ? '__not_reported__' : text;
}
export function matchesStatisticalMetadata(record, { analysis = null, design = null } = {}) {
  return (!analysis || metadataFilterKey(record.analysis_type) === analysis)
    && (!design || metadataFilterKey(record.study_design, 'study_design') === metadataFilterKey(design, 'study_design'));
}
export function statisticalMetadataOptions(data, field, filters = {}) {
  const options = new Map();
  data.links.flatMap(statisticalRecords).filter(record => matchesStatisticalMetadata(record, filters)).forEach(record => {
    const value = metadataFilterKey(record[field], field);
    const label = field === 'study_design' && value === 'prospective cohort' ? 'Prospective cohort' : String(record[field]).replace(/_/g, ' ').trim();
    if (!options.has(value)) options.set(value, { value, label: value === '__not_reported__' ? 'Not reported' : label, count: 0 });
    options.get(value).count++;
  });
  return [...options.values()].sort((a, b) => a.value === '__not_reported__' ? 1 : b.value === '__not_reported__' ? -1 : b.count - a.count || a.label.localeCompare(b.label));
}
